import {
  Activity,
  ArrowDownUp,
  BarChart3,
  BookOpen,
  Building2,
  ClipboardCheck,
  CreditCard,
  FileStack,
  GraduationCap,
  Image,
  LayoutDashboard,
  Layers,
  MessageSquare,
  Sparkles,
  UserCog,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export type AdminContentToolValue =
  | 'upload-media'
  | 'question-library'
  | 'daily-pulse-questions'
  | 'flow-builder'
  | 'flow-data'
  | 'course-builder'
  | 'course-data'
  | 'quiz-qa'
  | 'inspire-cards';

export type AdminContentTool = {
  value: AdminContentToolValue;
  label: string;
  icon: LucideIcon;
  separatorBefore?: boolean;
};

// Order and grouping mirror the former Content tab bar; `value` is the persisted `?tab=` URL value.
export const ADMIN_CONTENT_TOOLS: AdminContentTool[] = [
  { value: 'upload-media', label: 'Upload Media', icon: Image },
  { value: 'question-library', label: 'Question Library', icon: BookOpen, separatorBefore: true },
  { value: 'daily-pulse-questions', label: 'Daily Pulse Questions', icon: Activity },
  { value: 'flow-builder', label: 'Module Builder', icon: Layers, separatorBefore: true },
  { value: 'flow-data', label: 'Module Import/Export', icon: ArrowDownUp },
  { value: 'course-builder', label: 'Course Builder', icon: GraduationCap, separatorBefore: true },
  { value: 'course-data', label: 'Course Import/Export', icon: FileStack },
  { value: 'quiz-qa', label: 'Quiz QA', icon: ClipboardCheck, separatorBefore: true },
  { value: 'inspire-cards', label: 'Inspire Cards', icon: CreditCard, separatorBefore: true },
];

export const DEFAULT_ADMIN_CONTENT_TOOL: AdminContentToolValue = ADMIN_CONTENT_TOOLS[0].value;

export type AdminNavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  contentTool?: AdminContentToolValue;
  separatorBefore?: boolean;
};

export type AdminNavSection = {
  id: string;
  label?: string;
  items: AdminNavItem[];
};

export const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    id: 'home',
    items: [{ id: 'home', label: 'Admin Home', icon: LayoutDashboard, path: '/admin' }],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
      { id: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/admin/feedback' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: ADMIN_CONTENT_TOOLS.map((tool) => ({
      id: `content-${tool.value}`,
      label: tool.label,
      icon: tool.icon,
      path: '/admin/content',
      contentTool: tool.value,
      separatorBefore: tool.separatorBefore,
    })),
  },
  {
    id: 'people',
    label: 'People',
    items: [
      { id: 'users', label: 'Users', icon: UserCog, path: '/admin/users' },
      { id: 'organizations', label: 'Organizations', icon: Building2, path: '/admin/organizations' },
      { id: 'cohorts', label: 'Cohorts', icon: Users, path: '/admin/cohorts' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'ai-settings', label: 'AI Settings', icon: Sparkles, path: '/admin/ai-settings' },
      { id: 'operations', label: 'Operations', icon: Wrench, path: '/admin/operations' },
    ],
  },
];

// Every route that renders inside the admin sidebar layout (Content tools share `/admin/content`).
export const ADMIN_WORKSPACE_PATHS: string[] = Array.from(
  new Set(ADMIN_NAV_SECTIONS.flatMap((section) => section.items.map((item) => item.path))),
);

export const resolveAdminContentTool = (tabParam: string | null): AdminContentToolValue => {
  const normalized = tabParam === 'roleplay-builder' ? 'flow-builder' : tabParam;
  return ADMIN_CONTENT_TOOLS.find((tool) => tool.value === normalized)?.value ?? DEFAULT_ADMIN_CONTENT_TOOL;
};
